import * as Automerge from "@automerge/automerge";
import { isString, isObject, isArray, isNumber, get } from "lodash-es";
import diff from "fast-diff";

const types = {
  STRING: "String",
  OBJECT: "Object",
  ARRAY: "Array",
  NUMBER: "Number",
};

export function initDoc() {
  return Automerge.init();
}

export function initData(data) {
  let Doc = Automerge.init();
  const Types = [];
  for (const [key, value] of Object.entries(data)) {
    if (isString(value)) {
      Doc = Automerge.change(Doc, (newDoc: any) => {
        newDoc[key] = new Automerge.Text(value);
      });
      Types[key] = types.STRING;
    } else if (isNumber(value)) {
      const v = value.toString();
      Doc = Automerge.change(Doc, (newDoc: any) => {
        newDoc[key] = new Automerge.Text(v);
      });
      Types[key] = types.NUMBER;
    } else if (isArray(value)) {
      Doc = Automerge.change(Doc, (newDoc: any) => {
        const stringified = JSON.stringify(value);
        newDoc[key] = new Automerge.Text(stringified);
      });
      Types[key] = types.ARRAY;
    } else if (isObject(value)) {
      Doc = Automerge.change(Doc, (newDoc: any) => {
        const stringified = JSON.stringify(value);
        newDoc[key] = new Automerge.Text(stringified);
      });
      Types[key] = types.OBJECT;
    }
  }
  return { Doc, Types };
}

export function setValue(Doc, key, value) {
  const originalDoc = Doc.value;
  Doc.value = Automerge.change(originalDoc, (doc: any) => {
    const path = parsePath(key);
    const k = path[0];
    doc[k] = new Automerge.Text(value);
  });
}

function parsePath(path) {
  const keys = [];
  const regex = /([^[\].]+)/g;
  let match;

  // Get all parts of the path
  while ((match = regex.exec(path))) {
    keys.push(match[0]);
  }

  return keys;
}

export function getValue(doc, types, key) {
  const json = toJSON(doc, types);

  return get(json, key);
}

export function getDifferences(Doc, Types, key, oldValue, newValue) {
  const path = parsePath(key);
  const rootPath = path[0];
  let o = oldValue;
  let n = newValue;

  function applyValueFromPath(obj, keys, value) {
    const k = JSON.parse(JSON.stringify(keys));
    let last = k.pop();
    let current = obj;
    for (let key of k) {
      if (Array.isArray(current)) {
        const index = isNaN(key) ? current.findIndex((el) => el[key] !== undefined) : parseInt(key, 10);
        current = current[index];
      } else if (typeof current === "object" && current !== null) {
        current = current[key];
      }
    }
    current[last] = value;
    return obj;
  }

  if (!isString(o)) {
    o = o === null ? "" : JSON.stringify(o);
  }
  if (!isString(n)) {
    n = n === null ? "" : JSON.stringify(n);
  }
  if (path.length > 1 && (Types[rootPath] === types.OBJECT || Types[rootPath] === types.ARRAY)) {
    const updated = applyValueFromPath(toJSON(Doc, Types), path, newValue);
    return diff(Doc[rootPath].toString(), JSON.stringify(updated[rootPath]));
  }
  return diff(o, n);
}

export function updateDoc(Doc, types, key, differences) {
  const originalDoc = Doc.value;

  Doc.value = Automerge.change(originalDoc, (doc: any) => {
    const path = parsePath(key);
    const k = path[0];
    let str = doc[k];
    if (!str) {
      doc[k] = new Automerge.Text("");
      str = doc[k];
      types[k] = types.STRING;
    }

    let index = 0;

    differences.forEach(([op, v]) => {
      if (op === diff.EQUAL) {
        index += v.length;
      } else if (op === diff.INSERT) {
        str.insertAt(index, v);
      } else if (op === diff.DELETE) {
        str.deleteAt(index, v.length);
      }
    });
  });
}

export function isDifferent(differences) {
  if (differences.length === 0) {
    return false;
  } else if (differences.length == 1) {
    return differences[0][0] !== diff.EQUAL;
  }
  return true;
}

export function toJSON(Doc, Types) {
  const json = {};

  function convertStringToNumber(str) {
    if (str === "") {
      return null;
    }
    const num = Number(str);

    if (isNaN(num)) {
      return null;
    }

    return num;
  }

  for (const [key, value] of Object.entries(Doc)) {
    if (Types[key] === types.STRING) {
      json[key] = value.toString();
    } else if (Types[key] === types.NUMBER) {
      json[key] = convertStringToNumber(value.toString());
    } else if (Types[key] === types.OBJECT) {
      json[key] = JSON.parse(value);
    } else if (Types[key] === types.ARRAY) {
      json[key] = JSON.parse(value);
    }
  }
  return json;
}

export function encodeDoc(Doc) {
  return Automerge.save(Doc);
}

export function decodeDoc(doc) {
  const array: number[] = Object.values(doc);
  const docArray = new Uint8Array(array);
  return Automerge.load(docArray);
}

export const automerge = {
  initDoc: initDoc,
  updateDoc: updateDoc,
  setValue: setValue,
  isDifferent: isDifferent,
  initData: initData,
  getValue: getValue,
  toJSON: toJSON,
  getDifferences: getDifferences,
  encodeDoc: encodeDoc,
  decodeDoc: decodeDoc,
};
